/**
 * Expected failures travel as values, not exceptions: use cases answer
 * `Result<T, AppError>` so callers must deal with the failure (ADR 0006).
 * Unexpected failures keep throwing and end up in an error boundary.
 */
import { AppError } from './errors';

export type Result<T, E = AppError> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

/** A successful result. */
export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

/** A failed result. */
export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

export function isOk<T, E>(result: Result<T, E>): result is { ok: true; value: T } {
  return result.ok;
}

export function isErr<T, E>(result: Result<T, E>): result is { ok: false; error: E } {
  return !result.ok;
}

/** Maps the value of a successful result, leaving a failure untouched. */
export function mapResult<T, U, E>(result: Result<T, E>, map: (value: T) => U): Result<U, E> {
  return result.ok ? ok(map(result.value)) : result;
}

/** The value, or `fallback` when the result failed. */
export function unwrapOr<T, E>(result: Result<T, E>, fallback: T): T {
  return result.ok ? result.value : fallback;
}

/** Runs a promise-returning function and turns a thrown error into an {@link AppError} result. */
export async function attempt<T>(run: () => Promise<T>): Promise<Result<T, AppError>> {
  try {
    return ok(await run());
  } catch (error) {
    return err(AppError.from(error));
  }
}
